import type React from 'react';
import { useInView } from 'react-intersection-observer';
import { useIntl } from 'react-intl';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import { AI_SEARCH_INIT_PROMPT_PARAM } from '@xing-com/crate-jobs-constants';
import { useSerpsMatch } from '@xing-com/crate-jobs-domain-serps-hooks';
import { useIsAiSearch, useSearchState } from '@xing-com/crate-jobs-hooks';
import { usePageTracking } from '@xing-com/crate-jobs-tracking';
import {
  AISearchErrorBanner,
  AIEarnMoreDialog,
} from '@xing-com/crate-jobs-xinglets';
import { ScrollMemo } from '@xing-com/crate-layout-scroll-memo-module';
import { NoSSR } from '@xing-com/crate-utils-no-ssr';
import { XingletLoader } from '@xing-com/crate-xinglet';

import {
  FilterBottomSheet,
  AllFilters,
  FilterBar,
  Results,
  Header,
  BackButton,
  SearchBar,
  SearchBarLegacy,
} from './components';
import * as S from './container.styles';
import { useAddTypePolicies, useAIEarnMoreDialogProps } from './hooks';

export const Container: React.FC = () => {
  const { formatMessage } = useIntl();
  const isAiSearch = useIsAiSearch();
  const { isLoggedOut } = useLoginState();
  const serps = useSerpsMatch();
  const isSerps = Boolean(isLoggedOut && serps);
  const { isEmptySearch } = useSearchState();

  const { ref: searchBarRef, inView: isSearchBarVisible } = useInView({
    initialInView: true,
  });

  const aiEarnMoreDialogProps = useAIEarnMoreDialogProps();

  useAddTypePolicies();

  const { getSearchParameter } = useSearchParameter();
  const hasInitPrompt = !!getSearchParameter(AI_SEARCH_INIT_PROMPT_PARAM);
  const isEmptyState = isEmptySearch && !hasInitPrompt;

  usePageTracking({
    nwtOptions: {
      page: 'jobs_search/serp',
      flags: `jobs_search_ai_search_${isAiSearch}${isAiSearch ? `,jobs_search_empty_state_${isEmptyState}` : ''}`,
    },
  });

  return (
    <>
      <ScrollMemo />
      <div>
        <S.Background spacious={isAiSearch}>
          <Header isSticky={!isSearchBarVisible} />
          <S.SearchBarContainer variant="regular" $isLoggedOut={isLoggedOut}>
            <div ref={searchBarRef}>
              {isAiSearch ? <SearchBar /> : <SearchBarLegacy />}
            </div>
            <FilterBar />
          </S.SearchBarContainer>
        </S.Background>
        <S.ContentContainer variant="wide">
          <Results isStickyHeader={!isSearchBarVisible} />

          <S.LeftColumn>
            <nav>
              <BackButton />
            </nav>
            <aside
              aria-label={formatMessage({
                id: 'JOBS_SEARCH_FILTERS_ALL_HEADER',
              })}
            >
              <AllFilters />
            </aside>
          </S.LeftColumn>
        </S.ContentContainer>
        {isSerps ? (
          <XingletLoader name="@xing-com/crate-jobs-domain-serps-container" />
        ) : null}
        <NoSSR>
          <FilterBottomSheet />
        </NoSSR>
        <AISearchErrorBanner />
        <AIEarnMoreDialog {...aiEarnMoreDialogProps} />
      </div>
    </>
  );
};
